import { TYPING_DISPLAY_STALE_MS } from '~/lib/chat/activityStatus'
import { defineStore } from 'pinia'
import { invoke } from '~/lib/tauriInvoke'
import * as Sentry from '@sentry/nuxt'
import { useUserStore, type User } from '~/stores/user'
import { runAction } from '~/stores/storeErrorHandling'
import type { BotTypingState, BotTypingUser, EphemeralMessage, MentionedChannel, MentionedRole, MentionedUser } from '~/types/messages'
import { buildMentionLookups, resolveContentMentions } from '~/lib/chat/mentionTokens'
import { usePlatform } from '~/composables/usePlatform'
import { useGroupsStore } from '~/stores/groups'
import { ChannelType } from '~/types/groups'

export type {
  MessageAttachment,
  TypingUser,
  ReactionUsers,
  Message,
  BotTypingUser,
  BotTypingState,
  ForwardResult,
} from '~/types/messages'
import type { ForwardResult } from '~/types/messages'

// Initial-history page size, per form factor. This used to be a flat 100 (Telegram's own default),
// picked so a freshly opened channel's first page fills a typical viewport by itself and
// `MessageListInner.vue`'s scroll-to-top pagination almost never has to fire a second
// `get_channel_messages` right after the first one. That trade turned out to be the wrong one: a
// 100-message page mounts ~2000 components and every attachment/avatar in one go, which profiled as
// a multi-second stall behind the message-list skeleton on channel open (a single 1248ms rendering
// frame, 1161ms of it compositing). Paying for a slightly earlier second page is much cheaper than
// paying for that stall on every channel open.
//
// A phone's viewport holds far fewer messages than a desktop window, so it gets the smaller page.
export const INITIAL_MESSAGES_LIMIT_DESKTOP = 50
export const INITIAL_MESSAGES_LIMIT_MOBILE = 25

/**
 * Initial page size for the current form factor. A function rather than a constant because
 * `usePlatform()`'s `isMobile` is reactive — it also honours the `?mobile=1` dev override, so
 * previewing the mobile layout in a desktop browser gets the mobile page size too.
 */
export function getInitialMessagesLimit(): number {
  return usePlatform().isMobile.value ? INITIAL_MESSAGES_LIMIT_MOBILE : INITIAL_MESSAGES_LIMIT_DESKTOP
}

// The initial-page fetch a phone starts the moment a channel is tapped (pages/m-groups/[id].vue), while
// the chat panel's own code may still be loading. The panel takes it over once it mounts instead of
// fetching again (`takeChannelOpenRequest`): opening a channel stays one `get_channel_messages`,
// however late the panel turns up. Taken once, so a later open fetches afresh.
const channelOpenRequests = new Map<string, Promise<void>>()

// Default before/after window for `fetchMessagesAround` (jump-to-message) — enough context on
// both sides of the target that a reply-preview/forwarded-from click lands somewhere that reads
// as "the middle of a conversation", not a bare single bubble.
const AROUND_WINDOW = 20

// --- Gap-aware range tracking -----------------------------------------------------------------
// A channel's history can be loaded in disjoint pieces: the normal "current tail" + "load more"
// pagination, plus arbitrary "jump to message X" fetches that can land anywhere in the channel's
// history. `MessageRange` tracks each contiguously-known slice separately so a real gap between
// two loaded slices (messages that were never actually fetched) is never silently rendered as if
// it didn't exist. `messagesByChannel` (kept for the many existing direct readers across the
// app) is always the flattened, ascending concatenation of every range for that channel.
export interface MessageRange {
  messages: Message[]
  hasMoreBefore: boolean
  hasMoreAfter: boolean
}

export interface MergeEdge {
  extends: 'range-start' | 'range-end'
  rangeIndex: number
}

export interface MergeOptions {
  edge?: MergeEdge
  // Only ever applied to the specific boundary this fetch actually tested (the `edge` side, or —
  // for a brand new disjoint range — both sides) — never blindly stamped onto a range formed by
  // merging with already-loaded ranges, whose own boundaries this fetch didn't touch.
  hasMoreBefore?: boolean
  hasMoreAfter?: boolean
}

function compareMessages(a: Message, b: Message): number {
  const ac = a.created_at ?? ''
  const bc = b.created_at ?? ''
  if (ac !== bc) return ac < bc ? -1 : 1
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

// The server may echo a token's GUID in another case than the composer wrote it — the optimistic
// placeholder must still be recognised as the same message.
function normalizeMentionTokenCase(content: string): string {
  return content.replace(/<([@#])([0-9a-fA-F-]{36})>/g, (token) => token.toLowerCase())
}

function createLocalMessageId() {
  return `local-${globalThis.crypto?.randomUUID?.() ?? Date.now().toString(36)}`
}

function createEmptyUser(): User {
  return {
    id: '',
    username: '',
    email: '',
    display_name: '',
    description: '',
    status: '',
    preferred_language: '',
    is_confirmed: false,
    is_bot: false,
    subscription: null,
    avatar_url: '',
    banner_url: '',
  }
}

// Dedupes/coalesces concurrent `fetchMessagesAround` calls for the same (channelId, messageId)
// pair — deliberately module-level (not store state): it's pure request bookkeeping, not UI-
// relevant data, and doesn't need to be reactive or serialized.
const inFlightAroundRequests = new Map<string, Promise<{ found: boolean }>>()

export const useMessagesStore = defineStore('messages', {
  state: () => ({
    messagesByChannel: {} as Record<string, Message[]>,
    messageRangesByChannel: {} as Record<string, MessageRange[]>,
    hasMoreByChannel: {} as Record<string, boolean>,
    typingUsersByChannel: {} as Record<string, Record<string, TypingUser>>,
    botTypingByChannel: {} as Record<string, Record<string, BotTypingUser>>,
    // «Visible only to you» messages (see `EphemeralMessage`): memory only, never mixed into
    // `messagesByChannel`, so they don't touch read state, pagination or the cache.
    ephemeralByChannel: {} as Record<string, EphemeralMessage[]>,
    // Per-channel (unlike `error`, which is a scratch field for the *last* action across every
    // channel) — drives an inline "can't read this channel's history" state in the message list,
    // which needs to know specifically which channel failed, not just that something did. Also
    // reused for a failed `fetchMessagesAround` jump.
    messagesErrorByChannel: {} as Record<string, string | null>,
    isLoading: false,
    error: null as string | null,
  }),
  actions: {
    /** Logout: drop every loaded channel's messages (see `clearUserSession`). */
    resetForLogout() {
      inFlightAroundRequests.clear()
      this.$reset()
    },
    // --- range/flat-view bookkeeping ------------------------------------------------------------

    syncFlatMessages(channelId: string) {
      const ranges = this.messageRangesByChannel[channelId]
      this.messagesByChannel[channelId] = ranges ? ranges.flatMap((r) => r.messages) : []
      // "Load more" (scroll-to-top) always pages off the globally-earliest loaded message, i.e.
      // the first range's start boundary — see `handleLoadMore` in group/[id].vue.
      if (ranges && ranges.length > 0) {
        this.hasMoreByChannel[channelId] = ranges[0].hasMoreBefore
      }
    },

    findMessageById(channelId: string, messageId: string): Message | undefined {
      const ranges = this.messageRangesByChannel[channelId]
      if (!ranges) return undefined
      for (const range of ranges) {
        const found = range.messages.find((m) => m.id === messageId)
        if (found) return found
      }
      return undefined
    },

    findMessageAnyChannel(messageId: string): { channelId: string, message: Message } | undefined {
      for (const channelId in this.messageRangesByChannel) {
        const message = this.findMessageById(channelId, messageId)
        if (message) return { channelId, message }
      }
      return undefined
    },

    removeMessageById(channelId: string, messageId: string): boolean {
      const ranges = this.messageRangesByChannel[channelId]
      if (!ranges) return false
      for (const range of ranges) {
        const index = range.messages.findIndex((m) => m.id === messageId)
        if (index !== -1) {
          range.messages.splice(index, 1)
          this.messageRangesByChannel[channelId] = ranges.filter((r) => r.messages.length > 0)
          this.syncFlatMessages(channelId)
          return true
        }
      }
      return false
    },

    removeMessageAnyChannel(messageId: string): string | null {
      for (const channelId in this.messageRangesByChannel) {
        if (this.removeMessageById(channelId, messageId)) return channelId
      }
      return null
    },

    // The single merge primitive every mutation site funnels through — see the module-level doc
    // comment on `MessageRange` for why this exists. `incoming` is deduped against every existing
    // message id for this channel first (an already-loaded message is updated in place — server
    // fields like `is_edited`/`is_deleted`/`content` win, but a purely-local `status` — an
    // optimistic 'sending'/'failed' placeholder — survives if `incoming` doesn't specify one).
    //
    // Two ways a fetched block can be proven contiguous with something already loaded (the only
    // two ways — id *proximity* alone, e.g. two UUIDv7s that just look "close", is never treated
    // as proof, since a real unfetched gap could still sit between them):
    //   1. `opts.edge` — the caller fetched with an explicit boundary against an existing range
    //      (e.g. `beforeId` == that range's first message), which is contiguous by construction.
    //   2. A shared message id — if any message in `incoming` already lives in another range, that
    //      range and the new block both cover that message, so there's no gap between them either.
    // Every range that's provably contiguous this way ("touched") gets merged into one; anything
    // in `incoming` that doesn't touch anything becomes (or joins) a brand new, separate range.
    mergeMessagesIntoChannel(channelId: string, incoming: Message[], opts: MergeOptions = {}) {
      let ranges = this.messageRangesByChannel[channelId]
      if (!ranges) {
        ranges = []
        this.messageRangesByChannel[channelId] = ranges
      }

      if (incoming.length === 0) {
        if (opts.edge) {
          const r = ranges[opts.edge.rangeIndex]
          if (r) {
            if (opts.edge.extends === 'range-start' && opts.hasMoreBefore !== undefined) r.hasMoreBefore = opts.hasMoreBefore
            if (opts.edge.extends === 'range-end' && opts.hasMoreAfter !== undefined) r.hasMoreAfter = opts.hasMoreAfter
            // `hasMoreByChannel[channelId]` (what `MessageListInner.vue`'s auto-pagination
            // watchEffect actually reads) is only ever refreshed by `syncFlatMessages` — it does
            // NOT recompute itself just because a range object's `hasMoreBefore` field was mutated
            // in place. Skipping this call here (the empty-response path never used to reach it)
            // left `hasMore` stuck at whatever it was before forever, even once the range
            // genuinely learned "no more history" — the exact same empty response would then keep
            // being treated as reason to fetch again, forever, as fast as each round-trip resolves.
            this.syncFlatMessages(channelId)
          }
        } else if (ranges.length === 0 && opts.hasMoreBefore === false) {
          // An empty channel's first page: its whole history is known — there is none. There's no
          // range to hold that fact, so it goes straight to `hasMoreByChannel`; the first message
          // written here then opens its range on it (see the new-range branch below). Left unset,
          // that range assumed «more before» and the older-history placeholder (MessageListInner.vue)
          // came up above the only message, pulsing for as long as nothing scrolled it into loading.
          this.hasMoreByChannel[channelId] = false
          this.syncFlatMessages(channelId)
        }
        return
      }

      const edgeRange = opts.edge ? ranges[opts.edge.rangeIndex] : undefined
      const touched = new Set<MessageRange>()
      if (edgeRange) touched.add(edgeRange)

      const deduped: Message[] = []
      for (const msg of incoming) {
        let foundRange: MessageRange | undefined
        let foundIndex = -1
        for (const r of ranges) {
          const idx = r.messages.findIndex((m) => m.id === msg.id)
          if (idx !== -1) {
            foundRange = r
            foundIndex = idx
            break
          }
        }
        if (foundRange) {
          touched.add(foundRange)
          const existingMsg = foundRange.messages[foundIndex]
          foundRange.messages[foundIndex] = { ...existingMsg, ...msg, status: msg.status ?? existingMsg.status }
        } else {
          deduped.push(msg)
        }
      }

      if (deduped.length === 0 && touched.size === 0) return

      let combined: Message[]
      let hasMoreBefore: boolean
      let hasMoreAfter: boolean

      if (touched.size > 0) {
        const touchedArr = [...touched].sort((a, b) => compareMessages(a.messages[0], b.messages[0]))
        const seen = new Set<string>()
        combined = []
        for (const r of touchedArr) {
          for (const m of r.messages) {
            if (!seen.has(m.id)) {
              seen.add(m.id)
              combined.push(m)
            }
          }
        }
        for (const m of deduped) {
          if (!seen.has(m.id)) {
            seen.add(m.id)
            combined.push(m)
          }
        }
        combined.sort(compareMessages)

        const lowest = touchedArr[0]
        const highest = touchedArr[touchedArr.length - 1]
        hasMoreBefore = edgeRange === lowest && opts.edge?.extends === 'range-start' && opts.hasMoreBefore !== undefined
          ? opts.hasMoreBefore
          : lowest.hasMoreBefore
        hasMoreAfter = edgeRange === highest && opts.edge?.extends === 'range-end' && opts.hasMoreAfter !== undefined
          ? opts.hasMoreAfter
          : highest.hasMoreAfter

        ranges = ranges.filter((r) => !touched.has(r))
      } else {
        combined = [...deduped].sort(compareMessages)
        // Nothing loaded, and the start of history is known to be right here (an empty channel, or
        // every message of a fully loaded one deleted): this range starts that history.
        const historyKnownEmpty = ranges.length === 0 && this.hasMoreByChannel[channelId] === false
        hasMoreBefore = opts.hasMoreBefore ?? !historyKnownEmpty
        hasMoreAfter = opts.hasMoreAfter ?? true
      }

      const newRange: MessageRange = { messages: combined, hasMoreBefore, hasMoreAfter }
      const insertAt = ranges.findIndex((r) => compareMessages(r.messages[0], newRange.messages[0]) > 0)
      if (insertAt === -1) ranges.push(newRange)
      else ranges.splice(insertAt, 0, newRange)

      this.messageRangesByChannel[channelId] = ranges
      this.syncFlatMessages(channelId)
    },

    // A phone's channel open: see `channelOpenRequests`.
    openChannelMessages(channelId: string, limit: number, reason: string): Promise<void> {
      const request = this.fetchMessages(channelId, limit, undefined, reason)
      channelOpenRequests.set(channelId, request)
      request.catch(() => {}) // the panel taking it over reports it
      return request
    },

    takeChannelOpenRequest(channelId: string): Promise<void> | undefined {
      const request = channelOpenRequests.get(channelId)
      channelOpenRequests.delete(channelId)
      return request
    },

    // `reason` is diagnostic only (see the identical convention in `stores/groups.ts`'s
    // load* actions) — names the watcher/handler that triggered this fetch, since this action
    // always hits the backend (the `hasData` check below only affects the `isLoading` flag).
    async fetchMessages(channelId: string, limit: number = getInitialMessagesLimit(), beforeId?: string, reason = 'unknown', options: { boardChat?: boolean } = {}) {
      // A board channel opens as the board; its chat is loaded only by the board's chat panel (BoardChatPanel.vue,
      // `boardChat: true`) — never by the page/panel watchers that fire on selecting the channel.
      const groupsStore = useGroupsStore()
      const ownerGroupId = groupsStore.groupIdForChannel(channelId)
      if (!options.boardChat && ownerGroupId && groupsStore.findChannel(ownerGroupId, channelId)?.type === ChannelType.Board) return
      const hasData = !!this.messagesByChannel[channelId] && !beforeId
      if (!hasData) {
        this.isLoading = true
      }
      this.error = null
      if (!beforeId) this.messagesErrorByChannel[channelId] = null
      try {
        console.debug(`[MessagesStore] fetchMessages(${channelId}) reason=${reason} limit=${limit} beforeId=${beforeId ?? '-'} alreadyLoaded=${hasData}`)
        const messages = await invoke<Message[]>('get_channel_messages', {
          channelId,
          limit,
          beforeId,
        })
        console.log(`[MessagesStore] Received ${messages.length} messages`)
        const gotFullPage = messages.length === limit

        if (beforeId) {
          // "Load more" always pages off the globally-earliest loaded message (see
          // `syncFlatMessages`'s comment) — that message's range is the one this fetch's `id <
          // beforeId ORDER BY id DESC LIMIT N` result is guaranteed contiguous with by construction.
          const ranges = this.messageRangesByChannel[channelId] ?? []
          const rangeIndex = ranges.findIndex((r) => r.messages[0]?.id === beforeId)
          this.mergeMessagesIntoChannel(channelId, messages, {
            edge: rangeIndex !== -1 ? { extends: 'range-start', rangeIndex } : undefined,
            hasMoreBefore: gotFullPage,
          })
          if (messages.length === 0) {
            console.log(`[MessagesStore] Reached beginning of history for ${channelId}`)
          }
        } else {
          // No explicit edge here: this is "the current tail", which is only *provably* contiguous
          // with whatever's already loaded if it shares a message id with it (handled generically
          // by the merge itself) — not by assumption. If the channel has an unrelated older range
          // loaded (e.g. from a prior jump-to-message) with a real gap before "now", this correctly
          // lands as a second, separate range instead of fabricating a bridge across the gap.
          this.mergeMessagesIntoChannel(channelId, messages, { hasMoreBefore: gotFullPage, hasMoreAfter: false })
        }
      } catch (e: any) {
        const error = e?.message || String(e)
        this.error = error
        if (!beforeId) this.messagesErrorByChannel[channelId] = error
        // Otherwise MessageListInner's auto-pagination watchEffect (load more while the viewport
        // isn't scrollable, i.e. hasMore !== false) just keeps retrying the same failing request
        // forever — a permission error isn't going to resolve itself on the next attempt.
        this.hasMoreByChannel[channelId] = false
        Sentry.captureException(e, { extra: { action: 'fetchMessages', channelId, beforeId } })
        throw e
      } finally {
        this.isLoading = false
      }
    },

    // Jump-to-message: resolves a reply-preview/forwarded-from click whose target isn't currently
    // loaded, by fetching a window of context around it and merging it into the channel's ranges
    // via the same gap-aware primitive as everything else.
    async fetchMessagesAround(channelId: string, messageId: string, before = AROUND_WINDOW, after = AROUND_WINDOW): Promise<{ found: boolean }> {
      if (this.findMessageById(channelId, messageId)) return { found: true }

      const key = `${channelId}:${messageId}`
      const inFlight = inFlightAroundRequests.get(key)
      if (inFlight) return inFlight

      const promise = (async (): Promise<{ found: boolean }> => {
        try {
          // Both transports (`ClientSdk.ts`'s Tauri IPC and its REST/browser fallback) hand back
          // snake_case fields — see `restClient.ts`'s `camelToSnakeDeep` and the Rust DTOs' plain
          // (unrenamed) field names — same convention as e.g. `GetFriendsResponse.has_more`.
          const response = await invoke<{ messages: Message[], target_found: boolean }>('get_messages_around', {
            channelId,
            messageId,
            before,
            after,
          })

          const targetIndex = response.messages.findIndex((m) => m.id === messageId)
          const hasMoreBefore = targetIndex === -1 ? undefined : targetIndex === before
          const hasMoreAfter = targetIndex === -1 ? undefined : response.messages.length - targetIndex - 1 === after

          this.mergeMessagesIntoChannel(channelId, response.messages, { hasMoreBefore, hasMoreAfter })
          this.messagesErrorByChannel[channelId] = null
          return { found: response.target_found }
        } catch (e: any) {
          const error = e?.message || String(e)
          this.messagesErrorByChannel[channelId] = error
          Sentry.captureException(e, { extra: { action: 'fetchMessagesAround', channelId, messageId } })
          throw e
        } finally {
          inFlightAroundRequests.delete(key)
        }
      })()

      inFlightAroundRequests.set(key, promise)
      return promise
    },

    async sendMessage(channelId: string, content: string, replyTo?: string, attachmentIds: string[] = []) {
      // Mirrors ChatMessageInput's `canSend` gating here too, so any caller that skips (or has a stale)
      // UI check can't push an empty message: it needs text or at least one attachment.
      if (!content.trim() && attachmentIds.length === 0) return

      const userStore = useUserStore()
      // `content` already carries `<@id>` / `<#id>` tokens (the composer encodes them); the
      // placeholder resolves them from what this client knows until the server's copy replaces it.
      const { mentions, mention_channels } = resolveContentMentions(content, buildMentionLookups())
      const optimisticMessage: Message = {
        id: createLocalMessageId(),
        channel_id: channelId,
        author: userStore.user ?? createEmptyUser(),
        content,
        mentions,
        mention_channels,
        created_at: new Date().toISOString(),
        is_edited: false,
        is_deleted: false,
        reply_to: replyTo ?? '',
        forwarded_from: '',
        interaction_id: null,
        interaction_command_name: null,
        interaction_user_id: null,
        components_json: null,
        attachments: [],
        reactions: {},
        status: 'sending',
      }

      // A brand-new outgoing message is by definition adjacent to "now" — the frontier of the
      // newest loaded range (or the start of a new one if this channel has no ranges loaded yet).
      const appendToNewestRange = (message: Message) => {
        const ranges = this.messageRangesByChannel[channelId] ?? []
        const lastRangeIndex = ranges.length > 0 ? ranges.length - 1 : undefined
        this.mergeMessagesIntoChannel(channelId, [message], {
          edge: lastRangeIndex !== undefined ? { extends: 'range-end', rangeIndex: lastRangeIndex } : undefined,
          hasMoreAfter: false,
        })
      }

      appendToNewestRange(optimisticMessage)

      try {
        const message = await invoke<Message>('create_message', {
          payload: { channel_id: channelId, content, reply_to: replyTo, attachment_ids: attachmentIds },
        })
        // `local_id`: keeps the row's render key stable across the placeholder -> real id swap
        // (see `messageRenderKey`). Also survives the realtime-echo-first ordering below, since
        // the merge's update-in-place spreads this copy over the one the echo already added.
        const confirmed = { ...message, status: 'sent' as const, local_id: optimisticMessage.id }

        // The realtime (SignalR) channel can deliver this same message — under its real id —
        // before this invoke() round trip resolves, reconciling the optimistic placeholder itself
        // (see addRealtimeMessage below). Dropping the placeholder and merging the confirmed copy
        // (which is a no-op update-in-place if the realtime echo already added it) handles both
        // orderings without leaving a duplicate behind.
        this.removeMessageById(channelId, optimisticMessage.id)
        appendToNewestRange(confirmed)
        return message
      } catch (e: any) {
        const error = e?.message || String(e)
        this.error = error
        Sentry.captureException(e, { extra: { action: 'sendMessage', channelId } })

        const failedMessage: Message = { ...optimisticMessage, status: 'failed', error }
        const existing = this.findMessageById(channelId, optimisticMessage.id)
        if (existing) {
          existing.status = 'failed'
          existing.error = error
        }
        return failedMessage
      }
    },

    async retryMessage(messageId: string) {
      const found = this.findMessageAnyChannel(messageId)
      if (!found) return
      const { channelId, message: failedMessage } = found
      failedMessage.status = 'sending'
      failedMessage.error = undefined

      try {
        const message = await invoke<Message>('create_message', {
          payload: {
            channel_id: failedMessage.channel_id,
            content: failedMessage.content,
            reply_to: failedMessage.reply_to || undefined,
            attachment_ids: failedMessage.attachments.map((a) => a.id),
          },
        })
        // Same stable-key carry-over as `sendMessage` — the failed row was keyed by its local id.
        const confirmed = { ...message, status: 'sent' as const, local_id: failedMessage.local_id ?? messageId }
        this.removeMessageById(channelId, messageId)
        const ranges = this.messageRangesByChannel[channelId] ?? []
        const lastRangeIndex = ranges.length > 0 ? ranges.length - 1 : undefined
        this.mergeMessagesIntoChannel(channelId, [confirmed], {
          edge: lastRangeIndex !== undefined ? { extends: 'range-end', rangeIndex: lastRangeIndex } : undefined,
          hasMoreAfter: false,
        })
        return message
      } catch (e: any) {
        const error = e?.message || String(e)
        this.error = error
        Sentry.captureException(e, { extra: { action: 'retryMessage', messageId } })
        failedMessage.status = 'failed'
        failedMessage.error = error
        return failedMessage
      }
    },

    async updateMessage(messageId: string, content: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'updateMessage', extra: { messageId } },
        async () => {
          const updatedMessage = await invoke<Message>('update_message', {
            payload: { id: messageId, content },
          })
          const existing = this.findMessageById(updatedMessage.channel_id, messageId)
          if (existing) Object.assign(existing, updatedMessage)
          return updatedMessage
        },
      )
    },

    async forwardMessage(sourceMessageId: string, destinationChannelIds: string[]) {
      const idempotencyKey = globalThis.crypto.randomUUID()
      try {
        const response = await invoke<{ results: ForwardResult[] }>('forward_message', {
          payload: {
            source_message_id: sourceMessageId,
            destination_channel_ids: destinationChannelIds,
            idempotency_key: idempotencyKey,
          },
        })

        for (const result of response.results) {
          if (!result.success || !result.message) continue
          const channelId = result.channel_id
          const ranges = this.messageRangesByChannel[channelId] ?? []
          const lastRangeIndex = ranges.length > 0 ? ranges.length - 1 : undefined
          this.mergeMessagesIntoChannel(channelId, [result.message], {
            edge: lastRangeIndex !== undefined ? { extends: 'range-end', rangeIndex: lastRangeIndex } : undefined,
            hasMoreAfter: false,
          })
        }

        return response.results
      } catch (e: any) {
        const error = e?.message || String(e)
        this.error = error
        Sentry.captureException(e, { extra: { action: 'forwardMessage', sourceMessageId, destinationChannelIds } })
        throw e
      }
    },

    async fetchMessageById(messageId: string): Promise<Message | null> {
      try {
        return await invoke<Message>('get_message', { messageId })
      } catch (e: any) {
        return null
      }
    },

    async addReaction(messageId: string, emoji: string) {
      const userStore = useUserStore()
      const userId = userStore.user?.id
      if (!userId) return

      const found = this.findMessageAnyChannel(messageId)
      if (!found) return
      const { message } = found
      const previousReactions = message.reactions
      const existing = previousReactions[emoji]

      if (!existing?.user_ids.includes(userId)) {
        message.reactions = {
          ...previousReactions,
          [emoji]: { user_ids: [...(existing?.user_ids ?? []), userId] },
        }
      }

      try {
        const updated = await invoke<Message>('add_reaction', { payload: { message_id: messageId, emoji } })
        message.reactions = updated.reactions
      } catch (e: any) {
        message.reactions = previousReactions
        this.error = e?.message || String(e)
        Sentry.captureException(e, { extra: { action: 'addReaction', messageId, emoji } })
        throw e
      }
    },

    async removeReaction(messageId: string, emoji: string) {
      const userStore = useUserStore()
      const userId = userStore.user?.id
      if (!userId) return

      const found = this.findMessageAnyChannel(messageId)
      if (!found) return
      const { message } = found
      const previousReactions = message.reactions
      const existing = previousReactions[emoji]

      if (existing?.user_ids.includes(userId)) {
        const remainingUserIds = existing.user_ids.filter((id) => id !== userId)
        const nextReactions = { ...previousReactions }
        if (remainingUserIds.length > 0) {
          nextReactions[emoji] = { user_ids: remainingUserIds }
        } else {
          delete nextReactions[emoji]
        }
        message.reactions = nextReactions
      }

      try {
        const updated = await invoke<Message>('remove_reaction', { payload: { message_id: messageId, emoji } })
        message.reactions = updated.reactions
      } catch (e: any) {
        message.reactions = previousReactions
        this.error = e?.message || String(e)
        Sentry.captureException(e, { extra: { action: 'removeReaction', messageId, emoji } })
        throw e
      }
    },

    async toggleReaction(messageId: string, emoji: string) {
      const userStore = useUserStore()
      const userId = userStore.user?.id
      if (!userId) return

      const found = this.findMessageAnyChannel(messageId)
      if (!found) return
      const hasReacted = !!found.message.reactions[emoji]?.user_ids.includes(userId)
      if (hasReacted) {
        await this.removeReaction(messageId, emoji)
      } else {
        await this.addReaction(messageId, emoji)
      }
    },

    // Applies the authoritative reaction map pushed by a MessageReactionAdded/Removed SignalR event —
    // replaces (rather than merges with) the message's current reactions, since the event payload
    // already carries the full up-to-date map for that message.
    setMessageReactions(messageId: string, reactions: Record<string, string[]>) {
      const found = this.findMessageAnyChannel(messageId)
      if (!found) return
      found.message.reactions = Object.fromEntries(
        Object.entries(reactions).map(([emoji, userIds]) => [emoji, { user_ids: userIds }]),
      )
    },

    async deleteMessage(messageId: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'deleteMessage', extra: { messageId } },
        async () => {
          const response = await invoke<{ id: string; success: boolean }>('delete_message', { id: messageId })
          if (response.success) {
            this.removeMessageAnyChannel(messageId)
          }
        },
      )
    },

    addRealtimeMessage(message: Message) {
      const channelId = message.channel_id
      const ranges = this.messageRangesByChannel[channelId] ?? []
      const last = ranges[ranges.length - 1]

      // This can be the realtime echo of a message we just sent ourselves, arriving before
      // sendMessage()'s own invoke() call resolves — its optimistic placeholder is still sitting
      // in the newest range under a local temp id at this point. Reconcile with that placeholder
      // instead of merging a second bubble in; sendMessage() dedupes by real id too, so whichever
      // of the two resolves first wins without leaving a duplicate behind.
      if (last) {
        const pendingIndex = last.messages.findIndex(
          (m) => m.status === 'sending' && m.author?.id === message.author?.id
            && normalizeMentionTokenCase(m.content) === normalizeMentionTokenCase(message.content),
        )
        if (pendingIndex !== -1 && !last.messages.some((m) => m.id === message.id)) {
          // Keep the placeholder's id as the row's stable render key (see `messageRenderKey`).
          last.messages[pendingIndex] = { ...message, local_id: last.messages[pendingIndex].id }
          this.syncFlatMessages(channelId)
          return
        }
      }

      const lastRangeIndex = ranges.length > 0 ? ranges.length - 1 : undefined
      this.mergeMessagesIntoChannel(channelId, [message], {
        edge: lastRangeIndex !== undefined ? { extends: 'range-end', rangeIndex: lastRangeIndex } : undefined,
        hasMoreAfter: false,
      })
    },

    updateRealtimeMessage(
      messageId: string,
      content: string,
      componentsJson?: string | null,
      mentions?: {
        mentions: MentionedUser[]
        mention_channels: MentionedChannel[]
        mention_roles?: MentionedRole[]
        mention_everyone?: boolean
        mention_here?: boolean
      },
    ) {
      const found = this.findMessageAnyChannel(messageId)
      if (!found) return
      const { message } = found
      message.content = content
      // The edited content's tokens may name other users/channels than before.
      if (mentions) {
        message.mentions = mentions.mentions
        message.mention_channels = mentions.mention_channels
        if (mentions.mention_roles) message.mention_roles = mentions.mention_roles
        if (mentions.mention_everyone !== undefined) message.mention_everyone = mentions.mention_everyone
        if (mentions.mention_here !== undefined) message.mention_here = mentions.mention_here
      }
      message.is_edited = true
      // A bot's `UpdateMessage`/`InteractionResult(UPDATE_MESSAGE)` can also replace the
      // attached components — `undefined` (a plain human edit) leaves them untouched, an
      // explicit `null`/string replaces them.
      if (componentsJson !== undefined) message.components_json = componentsJson
    },

    deleteRealtimeMessage(messageId: string) {
      this.removeMessageAnyChannel(messageId)
    },

    setUserTyping(channelId: string, userId: string, username: string, isTyping: boolean) {
      if (isTyping) {
        const channelTyping = this.typingUsersByChannel[channelId] ?? {}
        channelTyping[userId] = { userId, username, updatedAt: Date.now() }
        this.typingUsersByChannel[channelId] = channelTyping
      } else {
        const channelTyping = this.typingUsersByChannel[channelId]
        if (channelTyping) delete channelTyping[userId]
      }
    },

    /** Adds an ephemeral message; without an anchor it sits after the channel's newest message. */
    addEphemeral(message: Omit<EphemeralMessage, 'anchor_id'> & { anchor_id?: string | null }) {
      const channelId = message.channel_id.toLowerCase()
      const list = this.ephemeralByChannel[channelId] ?? []
      if (list.some((m) => m.id === message.id)) return
      const anchor = message.anchor_id ?? message.reply_to ?? this.messagesByChannel[message.channel_id]?.at(-1)?.id ?? null
      this.ephemeralByChannel[channelId] = [...list, { ...message, channel_id: channelId, anchor_id: anchor }]
    },

    dismissEphemeral(channelId: string, id: string) {
      const key = channelId.toLowerCase()
      const list = this.ephemeralByChannel[key]
      if (list) this.ephemeralByChannel[key] = list.filter((m) => m.id !== id)
    },

    setBotTyping(channelId: string, botId: string, username: string, state: BotTypingState, isActive: boolean) {
      if (isActive) {
        const channelBots = this.botTypingByChannel[channelId] ?? {}
        const now = Date.now()
        // Same run of work (a stage change, not a fresh start): keep where and when it began.
        const previous = channelBots[botId]
        const sameRun = !!previous && now - previous.updatedAt < TYPING_DISPLAY_STALE_MS
        const newest = this.messagesByChannel[channelId]?.at(-1)
        channelBots[botId] = {
          botId,
          username,
          state,
          updatedAt: now,
          startedAt: sameRun ? previous.startedAt : now,
          anchorMessageId: sameRun ? previous.anchorMessageId : (newest?.id ?? null),
        }
        this.botTypingByChannel[channelId] = channelBots
      } else {
        const channelBots = this.botTypingByChannel[channelId]
        if (channelBots) delete channelBots[botId]
      }
    },
  },
})
